'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

type Locale = 'en' | 'ar';

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  isArabic: boolean;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('ar');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const queryLocale = params.get('lang');
    if (queryLocale === 'en' || queryLocale === 'ar') {
      setLocaleState(queryLocale);
      window.localStorage.setItem('locale', queryLocale);
      return;
    }

    const stored = window.localStorage.getItem('locale');
    if (stored === 'en' || stored === 'ar') {
      setLocaleState(stored);
      return;
    }

    const browserLang = (window.navigator.language || '').toLowerCase();
    if (browserLang.startsWith('ar')) {
      setLocaleState('ar');
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
    document.body.dir = locale === 'ar' ? 'rtl' : 'ltr';
    window.localStorage.setItem('locale', locale);
  }, [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale: setLocaleState,
      toggleLocale: () => setLocaleState((prev) => (prev === 'en' ? 'ar' : 'en')),
      isArabic: locale === 'ar',
    }),
    [locale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used inside I18nProvider');
  }
  return context;
}
